import React from "react";

import "./App.css";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { CarBackgroundBoxLayout } from "./CarBackgroundBoxLayout";
import { FormContainer, TextFieldElement } from "react-hook-form-mui";
import { sendMail, MAX_ATTACHMENT_BYTES } from "./MailGun";
import { Impressum } from "./Impressum";
import { Privacy } from "./Privacy";
import { makeStyles } from "./Theme";
import vehicleCatalog from "./vehicleCatalog.json";
import {
  createRequestId,
  InquiryPayload,
  InquiryRequestType,
} from "./InquiryPayload";

interface FormValues {
  name: string;
  email: string;
  phone: string;
  location: string;
  maker: string;
  model: string;
  mileage: string;
  initialRegistration: string;
}

interface DetailFormValues extends FormValues {
  vin: string;
  knownDefects: string;
  specialEquipment: string;
  accidentDamage: string;
  accidentDetails: string;
  serviceBook: string;
  tuvAu: string;
  holderCount: string;
  keyCount: string;
  vatDeductible: string;
  hasAskingPrice: string;
  askingPrice: string;
  desiredResultDate: string;
  tires: string[];
  documents: string[];
  catChecks: string[];
}

function createBaseInquiryPayload(
  requestType: InquiryRequestType,
  data: FormValues
): InquiryPayload {
  return {
    schemaVersion: 1,
    requestId: createRequestId(),
    requestType,
    submittedAt: new Date().toISOString(),
    source: "autogutverkaufen.de",
    contact: {
      name: data.name,
      email: data.email,
      phone: data.phone,
    },
    vehicle: {
      maker: data.maker,
      model: data.model,
      mileage: data.mileage,
      initialRegistration: data.initialRegistration,
      location: data.location,
    },
  };
}

function createDetailedInquiryPayload(
  baseData: FormValues,
  data: DetailFormValues
): InquiryPayload {
  const mergedData = detailToBaseFormValues(baseData, data);

  return {
    ...createBaseInquiryPayload("detailed", mergedData),
    details: {
      location: data.location,
      vin: data.vin,
      knownDefects: data.knownDefects,
      specialEquipment: data.specialEquipment,
      accidentDamage: data.accidentDamage,
      accidentDetails: data.accidentDetails,
      serviceBook: data.serviceBook,
      tuvAu: data.tuvAu,
      holderCount: data.holderCount,
      keyCount: data.keyCount,
      vatDeductible: data.vatDeductible,
      hasAskingPrice: data.hasAskingPrice,
      askingPrice: data.askingPrice,
      desiredResultDate: data.desiredResultDate,
      tires: data.tires,
      documents: data.documents,
      catChecks: data.catChecks,
    },
  };
}

const useStyles = makeStyles()((theme) => ({
  paperContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  sectionTitle: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    letterSpacing: "0.14em",
    [theme.breakpoints.down("sm")]: {
      letterSpacing: "0.1em",
    },
  },
  introCard: {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    background:
      "linear-gradient(135deg, rgba(255, 248, 237, 0.98), rgba(255, 255, 255, 0.95))",
    border: "1px solid rgba(255, 152, 0, 0.18)",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      borderRadius: theme.spacing(1.5),
    },
  },
  summaryCard: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    background: "rgba(255, 152, 0, 0.08)",
    border: "1px solid rgba(255, 152, 0, 0.18)",
  },
  actionButton: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  checklistAccordion: {
    border: "1px solid rgba(255, 152, 0, 0.18)",
    borderRadius: `${theme.spacing(1.5)} !important`,
    boxShadow: "none",
    overflow: "hidden",
    marginBottom: `${theme.spacing(2)} !important`,
    "&::before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  checklistGroup: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  checklistAccordionSummary: {
    background: "rgba(255, 152, 0, 0.08)",
    minHeight: theme.spacing(7),
    "&.Mui-expanded": {
      minHeight: theme.spacing(7),
    },
  },
  checklistAccordionDetails: {
    padding: theme.spacing(2.5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  yesNoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    borderRadius: theme.spacing(1.5),
    border: "1px solid rgba(0, 0, 0, 0.16)",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1.5),
    },
  },
  yesNoToggleGroup: {
    flexShrink: 0,
    "& .MuiToggleButton-root": {
      minWidth: theme.spacing(9),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      fontWeight: 700,
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& .MuiToggleButton-root": {
        flex: 1,
      },
    },
  },
}));

export type AppPages = "Form" | "DetailForm" | "Impressum" | "Privacy";
export type SendStatus = "sending" | "success" | "error" | undefined;
function App() {
  const [sendStatus, setSendStatus] = React.useState<SendStatus>();
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [content, setContent] = React.useState<AppPages>("Form");
  const [baseFormData, setBaseFormData] = React.useState<FormValues>();
  // Formular-State lebt auf App-Ebene, damit beim Wechsel zwischen
  // Kurzformular und Checkliste keine Eingaben verloren gehen.
  const [detailValues, setDetailValues] = React.useState<DetailFormValues>(
    () => createDetailFormValues()
  );
  const [imageFiles, setImageFiles] = React.useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = React.useState<string[]>([]);
  const { classes } = useStyles();

  // Navigation über Layout (Zurück-Button, Footer-Links): beim Wechsel
  // zur Startseite auch den Versandstatus zurücksetzen, damit das
  // Formular wieder nutzbar ist.
  const navigateTo = (page: AppPages) => {
    if (page === "Form") {
      setSendStatus(undefined);
      setErrorMessage(undefined);
    }
    setContent(page);
  };

  const continueToDetails = (data: FormValues) => {
    setBaseFormData(data);
    setDetailValues((currentValues) => ({ ...currentValues, ...data }));
    setSendStatus(undefined);
    setContent("DetailForm");
  };

  const generateProposal = (data: FormValues) => {
    const html = buildBaseHtml(
      "Es ist eine Schnellanfrage von AutoGutVerkaufen.de eingegangen.",
      data
    );

    sendProposal(
      html,
      "Schnellanfrage via AutoGutVerkaufen.de",
      [],
      createBaseInquiryPayload("quick", data)
    );
  };

  const generateDetailProposal = (
    baseData: FormValues,
    data: DetailFormValues,
    imageFiles: File[]
  ) => {
    const requestBaseData = detailToBaseFormValues(baseData, data);
    let html = buildBaseHtml(
      "Es ist eine Privatkundenauktion-Checkliste von AutoGutVerkaufen.de eingegangen.",
      requestBaseData
    );
    html += `<hr />`;
    html += `<h4>Privatkundenauktion - Checkliste</h4>`;
    // Standort steht bereits im Fahrzeug-Block der Basisdaten.
    html += mailLine("Fahrzeugidentnummer", data.vin);
    html += mailLine(
      "Bekannte technische Mängel / Sonstige Hinweise",
      data.knownDefects
    );
    html += mailLine("Sonderausstattung", data.specialEquipment);
    html += mailLine("Unfallschaden", data.accidentDamage);
    html += mailLine(
      "Wenn Unfallschaden, betroffene Bauteile und Schadenhöhe",
      data.accidentDetails
    );
    html += mailLine("Scheckheft", data.serviceBook);
    html += mailLine("TÜV / AU", data.tuvAu);
    html += mailLine("Anzahl der Halter", data.holderCount);
    html += mailLine("Anzahl Schlüssel", data.keyCount);
    html += mailListLine("Bereifung", data.tires);
    html += mailListLine("Fahrzeugpapiere", data.documents);
    html += mailListLine("Quick Car Check", data.catChecks);
    html += mailLine("MwSt. ausweisbar", data.vatDeductible);
    html += mailLine(
      "Gibt es einen Wunschpreis für den Sie das Fahrzeug sicher verkaufen werden?",
      data.hasAskingPrice
    );
    html += mailLine("Wunschpreis", data.askingPrice);
    html += mailLine(
      "Bis wann möchten Sie ein Ergebnis (TT/MM)?",
      data.desiredResultDate
    );
    if (imageFiles.length > 0) {
      html += mailLine("Bilder", `${imageFiles.length} Datei(en) angehängt`);
    }

    sendProposal(
      html,
      "Privatkundenauktion-Checkliste via AutoGutVerkaufen.de",
      imageFiles,
      createDetailedInquiryPayload(baseData, data)
    );
  };

  const sendProposal = async (
    html: string,
    subject: string,
    attachments: File[] = [],
    payload?: InquiryPayload
  ) => {
    setSendStatus("sending");
    setErrorMessage(undefined);
    try {
      const sendRes = await sendMail(
        "Neue Anfrage via AutoGutVerkaufen.de",
        html,
        attachments,
        subject,
        payload
      );
      if (sendRes.status === 200) {
        setSendStatus("success");
        // Anfrage abgeschlossen: Formular für eine neue Anfrage leeren.
        setBaseFormData(undefined);
        setDetailValues(createDetailFormValues());
        setImageFiles([]);
        setImagePreviews((previewUrls) => {
          previewUrls.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
          return [];
        });
        return;
      }

      let serverError: string | undefined;
      try {
        serverError = (await sendRes.json())?.error;
      } catch {
        // Antwort ohne JSON-Body (z. B. 413 der Function URL)
      }
      if (
        sendRes.status === 413 ||
        ["FILE_TOO_LARGE", "REQUEST_TOO_LARGE", "TOO_MANY_FILES"].includes(
          serverError ?? ""
        )
      ) {
        setErrorMessage(
          "Die Anhänge sind zusammen zu groß (max. ca. 4 MB). Bitte entfernen Sie einzelne Bilder und senden Sie erneut."
        );
      }
      setSendStatus("error");
    } catch (error) {
      console.error(error);
      if (error instanceof Error && error.message.includes("Anhänge")) {
        setErrorMessage(error.message);
      }
      setSendStatus("error");
    }
  };

  const showShortForm = () => {
    setSendStatus(undefined);
    setContent("Form");
  };

  let title = "";
  switch (content) {
    case "Privacy":
      title = "Datenschutzerklärung";
      break;
    case "DetailForm":
      title = "Privatkundenauktion - Checkliste";
      break;
    case "Form":
      title = "AutoGutVerkaufen.de";
      break;
    case "Impressum":
      title = "Impressum";
      break;

    default:
      title = "AutoGutVerkaufen.de";
      break;
  }
  return (
    <div className="App">
      <CarBackgroundBoxLayout
        setContent={navigateTo}
        content={content}
        title={title}
      >
        {content === "Form" && (
          <ContactForm
            sendStatus={sendStatus}
            errorMessage={errorMessage}
            baseFormData={baseFormData}
            continueToDetails={continueToDetails}
            classes={classes}
          />
        )}
        {content === "DetailForm" && (
          <DetailInseratForm
            sendStatus={sendStatus}
            errorMessage={errorMessage}
            baseFormData={baseFormData}
            detailValues={detailValues}
            setDetailValues={setDetailValues}
            imageFiles={imageFiles}
            setImageFiles={setImageFiles}
            imagePreviews={imagePreviews}
            setImagePreviews={setImagePreviews}
            generateProposal={generateProposal}
            generateDetailProposal={generateDetailProposal}
            classes={classes}
            showShortForm={showShortForm}
          />
        )}
        {content === "Impressum" && <Impressum setContent={navigateTo} />}
        {content === "Privacy" && <Privacy setContent={navigateTo} />}
      </CarBackgroundBoxLayout>
    </div>
  );
}

export default App;

function buildBaseHtml(intro: string, data: FormValues) {
  let html = escapeHtml(intro);
  html += `<hr />`;
  html += `<h4>Persönliche Daten</h4>`;
  html += mailLine("Name", data.name);
  if (data.phone?.trim()) {
    html += `<br /><strong>Telefon:</strong> <a href="tel:${escapeHtml(
      data.phone
    )}">${escapeHtml(data.phone)}</a>`;
  }
  if (data.email?.trim()) {
    html += `<br /><strong>eMail:</strong> <a href="mailto:${escapeHtml(
      data.email
    )}">${escapeHtml(data.email)}</a>`;
  }
  html += `<hr />`;
  html += `<h4>Fahrzeug</h4>`;
  html += mailLine("Marke", data.maker);
  html += mailLine("Modell", data.model);
  html += mailLine("Erstzulassung", data.initialRegistration);
  html += mailLine("KM-Stand", data.mileage);
  html += mailLine("Standort", data.location);

  return html;
}

function detailToBaseFormValues(
  baseData: FormValues,
  data: DetailFormValues
): FormValues {
  return {
    name: data.name || baseData.name,
    email: data.email || baseData.email,
    phone: data.phone || baseData.phone,
    location: data.location || baseData.location,
    maker: data.maker || baseData.maker,
    model: data.model || baseData.model,
    mileage: data.mileage || baseData.mileage,
    initialRegistration:
      data.initialRegistration || baseData.initialRegistration,
  };
}

function mailLine(label: string, value?: string) {
  const normalizedValue = value?.trim();

  if (!normalizedValue) {
    return "";
  }

  return `<br /><strong>${escapeHtml(label)}:</strong> ${escapeHtml(
    normalizedValue
  ).replace(/\n/g, "<br />")}`;
}

function mailListLine(label: string, value: string[]) {
  if (value.length === 0) {
    return "";
  }

  return mailLine(label, value.join(", "));
}

function escapeHtml(value: string | undefined) {
  return (value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function FormFeedback(props: {
  sendStatus: SendStatus;
  errorMessage?: string;
  onRetry?: () => void;
  onClose?: () => void;
}) {
  const { sendStatus, errorMessage, onRetry, onClose } = props;

  if (sendStatus === "sending") {
    return (
      <Alert
        severity="info"
        icon={<CircularProgress size={20} />}
        sx={{ textAlign: "left" }}
      >
        Ihre Anfrage wird gesendet. Bitte haben Sie einen Moment Geduld …
      </Alert>
    );
  }

  if (sendStatus === "success") {
    return (
      <Alert
        severity="success"
        sx={{ textAlign: "left" }}
        action={
          onClose ? (
            <Button
              color="inherit"
              size="small"
              onClick={onClose}
              sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
            >
              Zur Startseite
            </Button>
          ) : undefined
        }
      >
        Vielen Dank für Ihre Anfrage. Wir melden uns in Kürze bei Ihnen.
      </Alert>
    );
  }

  if (sendStatus === "error") {
    return (
      <Alert
        severity="error"
        sx={{ textAlign: "left" }}
        action={
          onRetry ? (
            <Button
              color="inherit"
              size="small"
              onClick={onRetry}
              sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
            >
              Erneut senden
            </Button>
          ) : undefined
        }
      >
        {errorMessage ??
          "Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."}{" "}
        Ihre Eingaben bleiben erhalten.
      </Alert>
    );
  }

  return null;
}

function ContactForm(props: {
  sendStatus: SendStatus;
  errorMessage?: string;
  baseFormData?: FormValues;
  continueToDetails: (data: FormValues) => void;
  classes: ReturnType<typeof useStyles>["classes"];
}) {
  const { sendStatus, errorMessage, baseFormData, continueToDetails, classes } =
    props;

  return (
    <Box className={classes.paperContainer}>
      <Stack spacing={{ xs: 3, sm: 4 }} sx={{ width: "100%" }}>
        <Box className={classes.introCard}>
          <Typography
            variant="h6"
            fontWeight={800}
            gutterBottom
            sx={{ fontSize: { xs: "1.15rem", sm: "1.25rem" } }}
          >
            Verkaufen Sie Ihr Auto unkompliziert.
          </Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.55 }}>
            Senden Sie uns die wichtigsten Fahrzeugdaten. Die Anfrage ist
            kostenlos und wir melden uns mit den nächsten Schritten.
          </Typography>
        </Box>

        <FormFeedback sendStatus={sendStatus} errorMessage={errorMessage} />

        {(sendStatus === undefined || sendStatus === "error") && (
          <FormContainer
            defaultValues={
              baseFormData ?? {
                name: "",
                email: "",
                phone: "",
                location: "",
                maker: "",
                model: "",
                mileage: "",
                initialRegistration: "",
              }
            }
            onSuccess={continueToDetails}
          >
            <Stack sx={{ gap: { xs: 2.5, sm: 4 }, textAlign: "left" }}>
              <FormSectionTitle classes={classes}>
                Kontakt und Fahrzeug
              </FormSectionTitle>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextFieldElement label="Name" name="name" fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextFieldElement
                    label="eMail"
                    required
                    name="email"
                    id="email"
                    fullWidth
                    parseError={() => "Bitte geben Sie eine eMail ein"}
                  />
                </Grid>
                <VehicleMakeModelFields />
              </Grid>

              <FormSectionTitle classes={classes}>
                Weitere Angaben
              </FormSectionTitle>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextFieldElement
                    label="Tel."
                    inputProps={{
                      inputMode: "tel",
                    }}
                    name="phone"
                    id="phone"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextFieldElement
                    label="Km-Stand"
                    name="mileage"
                    id="mileage"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MonthYearFieldElement
                    label="Erstzulassung"
                    name="initialRegistration"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextFieldElement
                    label="Fahrzeugstandort (PLZ / Ort)"
                    name="location"
                    id="location"
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Divider />

              <Stack spacing={2} alignItems="center">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.actionButton}
                >
                  Weiter
                </Button>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  Im nächsten Schritt können Sie freiwillig weitere Angaben und
                  Bilder ergänzen.
                </Typography>
              </Stack>
            </Stack>
          </FormContainer>
        )}
      </Stack>
    </Box>
  );
}

function MonthYearFieldElement(props: {
  name: keyof FormValues;
  label: string;
}) {
  const { name, label } = props;
  const { control } = useFormContext<FormValues>();

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (value) =>
          !value ||
          MONTH_YEAR_PATTERN.test(value) ||
          "Bitte im Format MM/JJJJ eingeben, z. B. 06/2018",
      }}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={field.value ?? ""}
          onChange={(event) =>
            field.onChange(formatMonthYear(event.target.value))
          }
          label={label}
          placeholder="MM/JJJJ"
          inputProps={{ inputMode: "numeric", maxLength: 7 }}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message}
          fullWidth
        />
      )}
    />
  );
}

// Lokale Referenzdaten von mobile.de; Aktualisierung via
// `node scripts/fetchVehicleCatalog.js`
const vehicleMakes: { key: string; label: string; models: string[] }[] =
  vehicleCatalog.makes;
const vehicleMakeLabels = vehicleMakes.map((make) => make.label);

function findModelsForMake(maker: string) {
  const normalizedMaker = maker.trim().toLocaleLowerCase();
  return (
    vehicleMakes.find(
      (make) => make.label.toLocaleLowerCase() === normalizedMaker
    )?.models ?? []
  );
}

function VehicleMakeModelFields() {
  const { control, setValue } = useFormContext<FormValues>();
  const maker = useWatch({ control, name: "maker" }) ?? "";
  const models = React.useMemo(() => findModelsForMake(maker), [maker]);

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Controller
          name="maker"
          control={control}
          render={({ field }) => (
            <Autocomplete<string, false, false, true>
              freeSolo
              options={vehicleMakeLabels}
              inputValue={field.value ?? ""}
              onChange={(_, value) => {
                field.onChange(value ?? "");
                setValue("model", "");
              }}
              onInputChange={(_, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  field.onChange(value);
                  setValue("model", "");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Marke"
                  placeholder="z. B. BMW"
                  fullWidth
                />
              )}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="model"
          control={control}
          render={({ field }) => (
            <Autocomplete<string, false, false, true>
              freeSolo
              options={models}
              inputValue={field.value ?? ""}
              onChange={(_, value) => field.onChange(value ?? "")}
              onInputChange={(_, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  field.onChange(value);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Modell"
                  placeholder={
                    maker ? "Modell auswählen oder eingeben" : "z. B. 3er"
                  }
                  fullWidth
                />
              )}
            />
          )}
        />
      </Grid>
    </>
  );
}

function DetailInseratForm(props: {
  sendStatus: SendStatus;
  errorMessage?: string;
  baseFormData: FormValues | undefined;
  detailValues: DetailFormValues;
  setDetailValues: React.Dispatch<React.SetStateAction<DetailFormValues>>;
  imageFiles: File[];
  setImageFiles: React.Dispatch<React.SetStateAction<File[]>>;
  imagePreviews: string[];
  setImagePreviews: React.Dispatch<React.SetStateAction<string[]>>;
  generateProposal: (data: FormValues) => void;
  generateDetailProposal: (
    baseData: FormValues,
    data: DetailFormValues,
    imageFiles: File[]
  ) => void;
  classes: ReturnType<typeof useStyles>["classes"];
  showShortForm: () => void;
}) {
  const {
    sendStatus,
    errorMessage,
    baseFormData,
    detailValues,
    setDetailValues,
    imageFiles,
    setImageFiles,
    imagePreviews,
    setImagePreviews,
    generateProposal,
    generateDetailProposal,
    classes,
    showShortForm,
  } = props;
  const [processingImages, setProcessingImages] = React.useState(false);
  const [attachmentWarning, setAttachmentWarning] = React.useState<string>();
  const [validationErrors, setValidationErrors] = React.useState<
    InvalidField[]
  >([]);
  const [emailTouched, setEmailTouched] = React.useState(false);

  const handleImagesChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files ?? [])
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, 10);
    event.target.value = "";

    setProcessingImages(true);
    setAttachmentWarning(undefined);
    try {
      const compressedFiles = await compressImagesToLimit(selectedFiles);
      const totalBytes = compressedFiles.reduce(
        (total, file) => total + file.size,
        0
      );
      if (totalBytes > MAX_ATTACHMENT_BYTES) {
        setAttachmentWarning(
          "Die ausgewählten Bilder sind auch nach Komprimierung zu groß (max. ca. 4 MB). Bitte wählen Sie weniger Bilder aus."
        );
      }

      imagePreviews.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
      setImageFiles(compressedFiles);
      setImagePreviews(
        compressedFiles.map((file) => URL.createObjectURL(file))
      );
    } finally {
      setProcessingImages(false);
    }
  };

  const submitRequest = (data: DetailFormValues) => {
    if (!baseFormData) {
      return;
    }

    const invalidFields = getInvalidDetailFields(data);
    setValidationErrors(invalidFields);
    if (invalidFields.length > 0) {
      focusField(invalidFields[0].field);
      return;
    }

    const hasOptionalDetails = hasChecklistDetails(data, imageFiles);

    if (hasOptionalDetails) {
      generateDetailProposal(baseFormData, data, imageFiles);
      return;
    }

    generateProposal(detailToBaseFormValues(baseFormData, data));
  };

  const setTextValue =
    (field: DetailTextField, format?: DetailFieldFormat) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const rawValue = event.target.value;
      const formattedValue =
        format === "monthYear"
          ? formatMonthYear(rawValue)
          : format === "dayMonth"
          ? formatDayMonth(rawValue)
          : rawValue;

      setValidationErrors((currentErrors) =>
        currentErrors.filter((invalid) => invalid.field !== field)
      );
      setDetailValues((currentValues) => {
        const nextValues = { ...currentValues, [field]: formattedValue };

        // Ohne Unfallschaden ist die Schadenbeschreibung hinfällig
        if (
          field === "accidentDamage" &&
          !accidentDetailsRequired(formattedValue)
        ) {
          nextValues.accidentDetails = "";
        }

        return nextValues;
      });
    };

  const setYesNoValue =
    (field: DetailTextField) =>
    (_: React.MouseEvent<HTMLElement>, value: string | null) => {
      setDetailValues((currentValues) => {
        const nextValues = { ...currentValues, [field]: value ?? "" };

        // Ohne Wunschpreis sind die Folgefragen hinfällig
        if (field === "hasAskingPrice" && value !== "Ja") {
          nextValues.askingPrice = "";
          nextValues.desiredResultDate = "";
        }

        return nextValues;
      });
    };

  const toggleListValue =
    (field: DetailListField, value: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDetailValues((currentValues) => {
        const currentFieldValues = currentValues[field];
        const nextFieldValues = event.target.checked
          ? [...currentFieldValues, value]
          : currentFieldValues.filter((fieldValue) => fieldValue !== value);

        return {
          ...currentValues,
          [field]: nextFieldValues,
        };
      });
    };

  // Nach erfolgreichem Versand wird baseFormData geleert — dann muss
  // hier die Erfolgsmeldung sichtbar bleiben, nicht dieser Hinweis.
  if (!baseFormData && sendStatus === undefined) {
    return (
      <Box className={classes.paperContainer}>
        <Stack spacing={3} alignItems="center" sx={{ width: "100%" }}>
          <Alert severity="info" sx={{ textAlign: "left", width: "100%" }}>
            Bitte geben Sie zuerst die Basisdaten ein.
          </Alert>
          <Button variant="contained" onClick={showShortForm}>
            Zurück
          </Button>
        </Stack>
      </Box>
    );
  }

  const renderTextField = (
    name: DetailTextField,
    label: string,
    options?: {
      multiline?: boolean;
      rows?: number;
      format?: DetailFieldFormat;
    }
  ) => {
    const format = options?.format;
    const value = detailValues[name];
    const formatError =
      format && value
        ? format === "monthYear"
          ? !MONTH_YEAR_PATTERN.test(value)
          : !DAY_MONTH_PATTERN.test(value)
        : false;
    // E-Mail erst nach Verlassen des Feldes (oder beim Absenden) prüfen,
    // damit während des Tippens keine Fehlermeldung aufblitzt.
    const emailError =
      name === "email" &&
      Boolean(value) &&
      !EMAIL_PATTERN.test(value) &&
      (emailTouched ||
        validationErrors.some((invalid) => invalid.field === "email"));

    return (
      <TextField
        label={label}
        name={name}
        value={value}
        onChange={setTextValue(name, format)}
        onBlur={name === "email" ? () => setEmailTouched(true) : undefined}
        fullWidth
        variant="outlined"
        multiline={options?.multiline}
        minRows={options?.rows}
        placeholder={
          format === "monthYear"
            ? "MM/JJJJ"
            : format === "dayMonth"
            ? "TT/MM"
            : undefined
        }
        inputProps={
          format
            ? {
                inputMode: "numeric",
                maxLength: format === "monthYear" ? 7 : 5,
              }
            : undefined
        }
        error={formatError || emailError}
        helperText={
          formatError
            ? format === "monthYear"
              ? "Bitte im Format MM/JJJJ eingeben, z. B. 06/2018"
              : "Bitte im Format TT/MM eingeben, z. B. 15/08"
            : emailError
            ? "Bitte geben Sie eine gültige E-Mail-Adresse ein"
            : undefined
        }
      />
    );
  };

  const renderSelectField = (
    name: DetailTextField,
    label: string,
    options: string[]
  ) => (
    <TextField
      select
      label={label}
      name={name}
      value={detailValues[name]}
      onChange={setTextValue(name)}
      fullWidth
      variant="outlined"
      SelectProps={{
        displayEmpty: true,
        renderValue: (selected) =>
          selected ? (
            <>{selected as string}</>
          ) : (
            <Typography component="span" color="text.disabled">
              Bitte auswählen
            </Typography>
          ),
      }}
      InputLabelProps={{ shrink: true }}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </TextField>
  );

  const renderYesNoToggle = (name: DetailTextField, label: string) => (
    <Box className={classes.yesNoRow}>
      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        color="primary"
        size="small"
        value={detailValues[name] || null}
        onChange={setYesNoValue(name)}
        className={classes.yesNoToggleGroup}
      >
        <ToggleButton value="Ja">Ja</ToggleButton>
        <ToggleButton value="Nein">Nein</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  return (
    <Box className={classes.paperContainer}>
      <Stack spacing={{ xs: 3, sm: 4 }} sx={{ width: "100%" }}>
        <Box className={classes.introCard}>
          <Typography
            variant="h6"
            fontWeight={800}
            gutterBottom
            sx={{ fontSize: { xs: "1.15rem", sm: "1.25rem" } }}
          >
            Privatkundenauktion - Checkliste
          </Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.55 }}>
            Bitte füllen Sie die folgenden Punkte so vollständig wie möglich
            aus.
          </Typography>
        </Box>

        <FormFeedback
          sendStatus={sendStatus}
          errorMessage={errorMessage}
          onRetry={() => submitRequest(detailValues)}
          onClose={showShortForm}
        />

        {(sendStatus === undefined || sendStatus === "error") && (
          <Box
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              submitRequest(detailValues);
            }}
          >
            <Stack sx={{ gap: { xs: 2.5, sm: 4 }, textAlign: "left" }}>
              <FormSectionTitle classes={classes}>
                Persönliche Daten
              </FormSectionTitle>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  {renderTextField("name", "Name *")}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderTextField("phone", "Telefonnummer für Rückfragen")}
                </Grid>
                <Grid item xs={12}>
                  {renderTextField("email", "E-Mail-Adresse")}
                </Grid>
                <Grid item xs={12}>
                  {renderTextField("location", "Fahrzeugstandort (PLZ / Ort)")}
                </Grid>
              </Grid>

              <FormSectionTitle classes={classes}>Auto Daten</FormSectionTitle>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  {renderTextField("mileage", "Laufleistung in KM")}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderTextField("initialRegistration", "Erstzulassung", {
                    format: "monthYear",
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderTextField("vin", "Fahrzeugidentnummer")}
                </Grid>
                <Grid item xs={12}>
                  {renderTextField(
                    "knownDefects",
                    "Bekannte technische Mängel / Sonstige Hinweise",
                    { multiline: true, rows: 4 }
                  )}
                </Grid>
                <Grid item xs={12}>
                  {renderTextField("specialEquipment", "Sonderausstattung", {
                    multiline: true,
                    rows: 4,
                  })}
                </Grid>
              </Grid>

              <FormSectionTitle classes={classes}>
                Fahrzeugzustand
              </FormSectionTitle>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} sm={6}>
                  {renderSelectField(
                    "accidentDamage",
                    "Unfallschaden",
                    accidentDamageOptions
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderSelectField(
                    "serviceBook",
                    "Scheckheft",
                    serviceBookOptions
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderTextField(
                    "tuvAu",
                    "TÜV / AU (Monat/Jahr; Bitte auch pflegen, wenn abgelaufen)",
                    { format: "monthYear" }
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderTextField(
                    "holderCount",
                    "Anzahl der Halter (bitte sich selbst auch mitzählen)"
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderTextField("keyCount", "Anzahl Schlüssel")}
                </Grid>
                {accidentDetailsRequired(detailValues.accidentDamage) && (
                  <Grid item xs={12}>
                    {renderTextField(
                      "accidentDetails",
                      "Betroffene Bauteile und Schadenhöhe",
                      { multiline: true, rows: 4 }
                    )}
                  </Grid>
                )}
              </Grid>

              <Box className={classes.checklistGroup}>
                <ChecklistAccordion
                  title="Quick Car Check"
                  description="Funktion und Testfahrt des Fahrzeugs"
                  classes={classes}
                >
                  <CheckboxGroup
                    title="CAT-Check"
                    values={catCheckOptions}
                    selectedValues={detailValues.catChecks}
                    onToggle={(value) => toggleListValue("catChecks", value)}
                  />
                </ChecklistAccordion>
                <ChecklistAccordion
                  title="Räder und Bereifung"
                  description="Bei 8-fach bitte beides ankreuzen"
                  classes={classes}
                >
                  <CheckboxGroup
                    title="Bereifung"
                    values={tireOptions}
                    selectedValues={detailValues.tires}
                    onToggle={(value) => toggleListValue("tires", value)}
                  />
                </ChecklistAccordion>
                <ChecklistAccordion
                  title="Fahrzeugpapiere"
                  description="Bitte vorhandene Kopien auswählen"
                  classes={classes}
                >
                  <CheckboxGroup
                    title="Dokumente"
                    values={documentOptions}
                    selectedValues={detailValues.documents}
                    onToggle={(value) => toggleListValue("documents", value)}
                  />
                </ChecklistAccordion>
              </Box>

              <FormSectionTitle classes={classes}>
                Zusätzliche Angaben
              </FormSectionTitle>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12}>
                  {renderYesNoToggle("vatDeductible", "MwSt. ausweisbar")}
                </Grid>
                <Grid item xs={12}>
                  {renderYesNoToggle(
                    "hasAskingPrice",
                    "Gibt es einen Wunschpreis für den Sie das Fahrzeug sicher verkaufen werden?"
                  )}
                </Grid>
                {detailValues.hasAskingPrice === "Ja" && (
                  <>
                    <Grid item xs={12} sm={6}>
                      {renderTextField("askingPrice", "Wunschpreis in €")}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderTextField(
                        "desiredResultDate",
                        "Ergebnis gewünscht bis (TT/MM)",
                        { format: "dayMonth" }
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Idealerweise mindestens 2 Tage im Voraus und nicht am
                        Wochenende oder Feiertag.
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>

              <Box>
                <FormSectionTitle classes={classes}>
                  Hier können Sie Fahrzeugbilder, Rechnung, Gutachten etc. hochladen
                </FormSectionTitle>
                <Stack spacing={2} alignItems="flex-start">
                  <Button variant="outlined" component="label">
                    Bilder auswählen
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImagesChange}
                    />
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {processingImages
                      ? "Bilder werden verarbeitet …"
                      : imageFiles.length > 0
                      ? `${imageFiles.length} Bild(er) ausgewählt`
                      : "Optional: Bilder, Rechnung, Gutachten und weitere Unterlagen."}
                  </Typography>
                  {attachmentWarning && (
                    <Alert severity="warning" sx={{ textAlign: "left" }}>
                      {attachmentWarning}
                    </Alert>
                  )}
                  {imagePreviews.length > 0 && (
                    <Grid container spacing={1.5}>
                      {imagePreviews.map((previewUrl, index) => (
                        <Grid item xs={4} sm={3} key={previewUrl}>
                          <Box
                            component="img"
                            src={previewUrl}
                            alt={`Fahrzeugbild ${index + 1}`}
                            sx={{
                              width: "100%",
                              aspectRatio: "1",
                              objectFit: "cover",
                              borderRadius: 2,
                              border: "1px solid rgba(0, 0, 0, 0.08)",
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Stack>
              </Box>

              <Divider />

              {validationErrors.length > 0 && (
                <Alert severity="error" sx={{ textAlign: "left" }}>
                  Bitte korrigieren Sie folgende Felder:{" "}
                  {validationErrors.map((invalid, index) => (
                    <React.Fragment key={invalid.field}>
                      {index > 0 && ", "}
                      <Link
                        component="button"
                        type="button"
                        onClick={() => focusField(invalid.field)}
                        sx={{ fontWeight: 700, verticalAlign: "baseline" }}
                      >
                        {invalid.label}
                      </Link>
                    </React.Fragment>
                  ))}
                </Alert>
              )}

              <Stack spacing={2} alignItems="center">
                <Button
                  disabled={processingImages}
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.actionButton}
                >
                  {sendStatus === "error"
                    ? "Erneut senden"
                    : "Anfrage abschicken"}
                </Button>
                <Button
                  type="button"
                  variant="text"
                  onClick={showShortForm}
                  className={classes.actionButton}
                >
                  Zurück
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// Handyfotos (oft 5–10 MB) clientseitig verkleinern, damit auch zehn
// Bilder unter das Payload-Limit der Lambda Function URL passen.
const IMAGE_COMPRESSION_PASSES = [
  { maxDimension: 1920, quality: 0.8 },
  { maxDimension: 1280, quality: 0.7 },
];

async function compressImage(
  file: File,
  maxDimension: number,
  quality: number
): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(
      1,
      maxDimension / Math.max(bitmap.width, bitmap.height)
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    if (!blob || blob.size >= file.size) {
      return file;
    }
    return new File([blob], file.name.replace(/\.[^.]*$/, "") + ".jpg", {
      type: "image/jpeg",
    });
  } catch {
    // z. B. nicht dekodierbares Format – Original unverändert lassen
    return file;
  }
}

async function compressImagesToLimit(files: File[]): Promise<File[]> {
  let currentFiles = files;
  for (const pass of IMAGE_COMPRESSION_PASSES) {
    const totalBytes = currentFiles.reduce(
      (total, file) => total + file.size,
      0
    );
    if (totalBytes <= MAX_ATTACHMENT_BYTES) {
      return currentFiles;
    }
    currentFiles = await Promise.all(
      currentFiles.map((file) =>
        compressImage(file, pass.maxDimension, pass.quality)
      )
    );
  }
  return currentFiles;
}

type DetailListField = "tires" | "documents" | "catChecks";
type DetailTextField = Exclude<keyof DetailFormValues, DetailListField>;
type DetailFieldFormat = "monthYear" | "dayMonth";

const MONTH_YEAR_PATTERN = /^(0[1-9]|1[0-2])\/\d{4}$/;
const DAY_MONTH_PATTERN = /^(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface InvalidField {
  field: DetailTextField;
  label: string;
}

function getInvalidDetailFields(data: DetailFormValues): InvalidField[] {
  const invalidFields: InvalidField[] = [];

  if (data.email && !EMAIL_PATTERN.test(data.email)) {
    invalidFields.push({ field: "email", label: "E-Mail-Adresse" });
  }
  if (
    data.initialRegistration &&
    !MONTH_YEAR_PATTERN.test(data.initialRegistration)
  ) {
    invalidFields.push({
      field: "initialRegistration",
      label: "Erstzulassung",
    });
  }
  if (data.tuvAu && !MONTH_YEAR_PATTERN.test(data.tuvAu)) {
    invalidFields.push({ field: "tuvAu", label: "TÜV / AU" });
  }
  if (
    data.desiredResultDate &&
    !DAY_MONTH_PATTERN.test(data.desiredResultDate)
  ) {
    invalidFields.push({
      field: "desiredResultDate",
      label: "Ergebnis gewünscht bis",
    });
  }

  return invalidFields;
}

function focusField(field: DetailTextField) {
  const element = document.querySelector<HTMLElement>(`[name="${field}"]`);
  element?.scrollIntoView({ behavior: "smooth", block: "center" });
  element?.focus({ preventScroll: true });
}

function formatMonthYear(input: string) {
  let digits = input.replace(/\D/g, "");

  // "3" -> "03", damit direkt der Monat vervollständigt wird
  if (digits.length >= 1 && Number(digits[0]) > 1) {
    digits = `0${digits}`;
  }

  digits = digits.slice(0, 6);

  if (digits.length <= 2) {
    return digits;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function formatDayMonth(input: string) {
  let digits = input.replace(/\D/g, "");

  // "4" -> "04", damit direkt der Tag vervollständigt wird
  if (digits.length >= 1 && Number(digits[0]) > 3) {
    digits = `0${digits}`;
  }

  digits = digits.slice(0, 4);

  if (digits.length <= 2) {
    return digits;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

const accidentDamageOptions = [
  "nicht bekannt",
  "reparierter Unfallschaden",
  "Unfallschaden nicht repariert (aktueller Unfallschaden)",
  "unfallfrei",
];
const serviceBookOptions = [
  "Scheckheft gepflegt",
  "Scheckheft nicht gepflegt",
  "Scheckheft nicht vorhanden",
];

function accidentDetailsRequired(accidentDamage: string) {
  return (
    accidentDamage === "reparierter Unfallschaden" ||
    accidentDamage === "Unfallschaden nicht repariert (aktueller Unfallschaden)"
  );
}

const tireOptions = ["Sommer", "Winter", "Allwetter"];

const documentOptions = [
  "Fahrzeugbriefkopie (bitte unbedingt als Foto beilegen)",
  "Fahrzeugscheinkopie (bitte unbedingt als Foto beilegen)",
];

const catCheckOptions = [
  "Motor läuft (Motor ohne Auffälligkeiten gestartet, keine Unregelmäßigkeiten, keine außergewöhnlichen Geräusche)",
  "Getriebe funktioniert (Alle Gänge inkl. Rückwärtsgang im Stand ohne Auffälligkeiten durchgeschalten)",
  "El. Fensterheber funktionieren (Alle el. Fensterheber funktionieren)",
  "Klimaanalage funktioniert (Klimaanlage kühlt)",
  "Bordelektronik funktioniert (Gesamte Bordelektronik funktioniert ohne Unregelmäßigkeiten)",
  "Testfahrt bis ca. 30Km/h (ohne Auffälligkeiten; Motor, Getriebe und Bremswirkung in Ordnung)",
  "Testfahrt bis ca. 50Km/h (ohne Auffälligkeiten; Motor, Getriebe und Bremswirkung in Ordnung)",
  "Testfahrt bis ca. 120Km/h (ohne Auffälligkeiten; Motor, Getriebe und Bremswirkung in Ordnung)",
];

const checklistDetailFields: DetailTextField[] = [
  "location",
  "vin",
  "knownDefects",
  "specialEquipment",
  "accidentDamage",
  "accidentDetails",
  "serviceBook",
  "tuvAu",
  "holderCount",
  "keyCount",
  "vatDeductible",
  "hasAskingPrice",
  "askingPrice",
  "desiredResultDate",
];

function createDetailFormValues(baseFormData?: FormValues): DetailFormValues {
  return {
    name: baseFormData?.name ?? "",
    email: baseFormData?.email ?? "",
    phone: baseFormData?.phone ?? "",
    maker: baseFormData?.maker ?? "",
    model: baseFormData?.model ?? "",
    mileage: baseFormData?.mileage ?? "",
    initialRegistration: baseFormData?.initialRegistration ?? "",
    location: "",
    vin: "",
    knownDefects: "",
    specialEquipment: "",
    accidentDamage: "",
    accidentDetails: "",
    serviceBook: "",
    tuvAu: "",
    holderCount: "",
    keyCount: "",
    vatDeductible: "",
    hasAskingPrice: "",
    askingPrice: "",
    desiredResultDate: "",
    tires: [],
    documents: [],
    catChecks: [],
  };
}

function hasChecklistDetails(data: DetailFormValues, imageFiles: File[]) {
  return (
    imageFiles.length > 0 ||
    checklistDetailFields.some((field) => {
      const value = data[field].trim();

      return value !== "" && value !== "Bitte auswählen";
    }) ||
    data.tires.length > 0 ||
    data.documents.length > 0 ||
    data.catChecks.length > 0
  );
}

function ChecklistAccordion(props: {
  title: string;
  description: string;
  classes: ReturnType<typeof useStyles>["classes"];
  children: React.ReactNode;
}) {
  return (
    <Accordion disableGutters className={props.classes.checklistAccordion}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={props.classes.checklistAccordionSummary}
      >
        <Box>
          <Typography fontWeight={700}>{props.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {props.description}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails className={props.classes.checklistAccordionDetails}>
        {props.children}
      </AccordionDetails>
    </Accordion>
  );
}

function CheckboxGroup(props: {
  title: string;
  values: string[];
  selectedValues: string[];
  onToggle: (
    value: string
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {props.title}
      </Typography>
      <FormGroup sx={{ gap: 0.5 }}>
        {props.values.map((value) => (
          <FormControlLabel
            key={value}
            control={
              <Checkbox
                checked={props.selectedValues.includes(value)}
                onChange={props.onToggle(value)}
              />
            }
            label={value}
            sx={{
              alignItems: "flex-start",
              margin: 0,
              "& .MuiCheckbox-root": { paddingTop: 0.25 },
              "& .MuiFormControlLabel-label": { lineHeight: 1.45 },
            }}
          />
        ))}
      </FormGroup>
    </Box>
  );
}

function FormSectionTitle(props: {
  children: React.ReactNode;
  classes: ReturnType<typeof useStyles>["classes"];
}) {
  return (
    <Typography
      variant="overline"
      display="block"
      className={props.classes.sectionTitle}
    >
      {props.children}
    </Typography>
  );
}
